export const PersonalizationTypes = {
  TEXT: 'EMBROIDERED_LETTERING',
  LOGO: 'EMBROIDERED_LOGO',
};

export const StateFields = {
  // text customization fields
  LINE_1: 'line1',
  LINE_2: 'line2',
  COLOR: 'color',
  FONT: 'font',
  TEXT_LOCATION: 'textLocation',
  TEXT_CUSTOMIZATIONS: 'textCustomizations',

  // icon customization fields
  ICON: 'icon',
  ICON_LOCATION: 'iconLocation',
  ICON_CUSTOMIZATIONS: 'iconCustomizations',

  // image preview fields
  PREVIEW_LOADING: 'previewImageLoading',
  PREVIEW_IMAGES: 'previewImages',
  PREVIEW_ERROR: 'previewError',

  // embroidery step field
  CURRENT_IDX: 'currentIdx',
};

export const Progress = {
  CONTINUE: 1,
  BACK: 2,
  RESET: 3,
};

export const TEXT_PAGE = 'textPage';
export const ICON_PAGE = 'iconPage';
export const CONFIRMATION_PAGE = 'confirmationPage';

export const TEXT_PAGE_ID = 1;
export const ICON_PAGE_ID = 2;

export const INITIAL_PAGE = 0;
export const LOGO = 'logo';

const TEXT_CUSTOMIZATION_IDS = new Set(['line1', 'line2']);

/**
 * @function getLocation
 *
 * Maps a location id to the customization interface. Convenience method
 * that makes it easier to flatten all customizations and customization location ids
 * into a single object.
 *
 * @param {{
 *   locationId: number,
 *   locationLabel: string,
 *   customizations: {customizationId: string, value: string}[]
 * }} ctx - user selected customizations
 *
 * @returns {{id: 'textLocation' | 'logoLocation', value: number }}
 */
export const getLocation = (ctx) => {
  if (ctx.type) {
    return ctx.type === PersonalizationTypes.TEXT
      ? { id: 'textLocation', value: ctx.locationId || ctx.locationLabel }
      : { id: 'logoLocation', value: ctx.locationId };
  } else {
    // find any text customization
    const found = ctx.customizations.find(
      ({ customizationId, id }) =>
        TEXT_CUSTOMIZATION_IDS.has(customizationId) ||
        TEXT_CUSTOMIZATION_IDS.has(id)
    );

    return found
      ? { id: 'textLocation', value: ctx.locationId || ctx.locationLabel }
      : { id: 'logoLocation', value: ctx.locationId };
  }
};

/**
 * @function parseEmbroideryDetails
 *
 * Flattens an array of user selected embroidery customizations to a single object.
 * Used to parse embroidery details on PDP post selection, on a cart item and on an
 * order item.
 *
 * @param {{
 *   locationId: number,
 *   locationLabel: string,
 *   customizations: {customiztionId: string, value: string}[]
 * }[]} embroideryContext - array of user selected customizations
 *
 * @returns {{
 *   textLocation?: number,
 *   font?: string,
 *   color?: string,
 *   line1?: string,
 *   line2?: string,
 *   locationLabel?: string,
 *   logoLocation?: number,
 *   logo?: number
 * }} - {
 *   textLocation?: number,
 *   font?: string,
 *   color?: string,
 *   line1?: string,
 *   line2?: string,
 *   locationLabel?: string,
 *   logoLocation?: number,
 *   logo?: number
 * }
 */
export const parseEmbroideryDetails = (embroideryContext) => {
  return Array.isArray(embroideryContext) && !!embroideryContext.length
    ? embroideryContext.reduce((acc, ctx) => {
        [...ctx.customizations, getLocation(ctx)].forEach(
          ({ customizationId, id, value }) => {
            acc[customizationId || id] = value;
          }
        );
        return acc;
      }, {})
    : [];
};

/**
 * @function getPrices
 *
 * Determines price for customization options.
 * Prefers promoPrice, but will fallback to unitPrice.
 *
 * @param {{
 *   type: PersonalizationTypes.TEXT | PersonalizationType.LOGO,
 *   promoPrice: number,
 *   unitPrice: number,
 *   locations: {
 *     locationId: number,
 *     customizations: {
 *       id: string,
 *       options: {
 *         optionId: number,
 *         label: string
 *       }[]
 *     }[],
 *   }[],
 * }[]
 * } customizations -  customizations product personalization options - array of available customizations
 *
 * @returns {{
 *   'EMBROIDERED_LETTERING': number,
 *   'EMBROIDERED_LOGO': number
 * }} - {
 *   'EMBROIDERED_LETTERING': number,
 *   'EMBROIDERED_LOGO': number
 * }
 */
export const getPrices = (customizations) =>
  customizations.reduce((acc, customization) => {
    if (!customization) {
      return acc;
    }
    acc[customization.type] =
      customization.promoPrice ?? customization.unitPrice;
    return acc;
  }, {});

/**
 * @function getIconCustomizationOptions
 *
 * @param {{
 *   type: PersonalizationType.LOGO,
 *   promoPrice: number,
 *   unitPrice: number,
 *   locations: {
 *     locationId: number,
 *     customizations: {
 *       id: string,
 *       options: {
 *         optionId: number,
 *         label: string
 *       }[]
 *     }[],
 *   }[],
 * }[]} iconPersonalizations
 *
 * @param {number} logoLocation
 *
 * @returns {{
 *   customizationId: 'logo',
 *   options: {
 *    optionId: number,
 *    label: string
 *   }[]
 * }} - {
 *   customizationId: 'logo',
 *   options: {
 *    optionId: number,
 *    label: string
 *   }[]
 * }
 */
export const getIconCustomizationOptions = (
  iconPersonalizations,
  logoLocation
) => {
  const found = iconPersonalizations.locations.find(
    ({ locationId }) => locationId === parseInt(logoLocation, 10)
  );

  return found?.customizations.find(
    ({ customizationId }) => customizationId === 'logo'
  );
};

/**
 * @function mapEmbroideryDetails
 * Maps embroidery detail number ids to their display string values (ex. 1 -> 'Red' or 2 -> 'Script')
 * @param {{
 *   type: 'EMBROIDERED_LOGO',
 *   locations: {
 *     locationId: number,
 *     customizations: {
 *       id: string,
 *       options: {
 *         optionId: number,
 *         label: number
 *       }[]
 *     }[]
 *   }[]
 * }} iconPersonalizations
 * @param {{
 *   type: 'EMBROIDERED_LETTERING',
 *   locations: {
 *     locationId: number,
 *     customizations: {
 *       id: string,
 *       options: {
 *         optionId: number,
 *         label: string
 *       }[]
 *     }[]
 *   }[]
 * }} textPersonalizations
 * @param {{
 *   textLocation?: number,
 *   font?: string,
 *   color?: string,
 *   line1?: string,
 *   line2?: string,
 *   logoLocation?: number,
 *   logo?: number
 * }} parsedDetails
 *
 * @returns {{
 *   textLocation?: number,
 *   font?: string,
 *   color?: string,
 *   line1?: string,
 *   line2?: string,
 *   logoLocation?: number,
 *   logo?: string
 * }} - {
 *   textLocation?: number,
 *   font?: string,
 *   color?: string,
 *   line1?: string,
 *   line2?: string,
 *   logoLocation?: number,
 *   logo?: string
 * }
 */
export const mapEmbroideryDetails = (
  iconPersonalizations,
  textPersonalizations,
  parsedDetails
) => {
  // make sure these are ints and not strings.
  // if these values are strings, there's no need to map,
  const fontIsNan = isNaN(parseInt(parsedDetails.font, 10));
  const colorIsNan = isNaN(parseInt(parsedDetails.color, 10));
  const logoIsNan = isNaN(parseInt(parsedDetails.logo, 10));

  if (fontIsNan && colorIsNan && logoIsNan) {
    return parsedDetails;
  }

  const logoOptions =
    parsedDetails.logo && parsedDetails.logoLocation
      ? getIconCustomizationOptions(
          iconPersonalizations,
          parsedDetails.logoLocation
        )
      : null;

  const { customizations: textCustomizations } =
    (parsedDetails.line1 || parsedDetails.line2) && parsedDetails.textLocation
      ? textPersonalizations.locations.find(
          ({ locationId }) =>
            locationId === parseInt(parsedDetails.textLocation, 10)
        )
      : {};

  const flatLogoCustomizations = logoOptions
    ? logoOptions.options.reduce((acc, { optionId, label }) => {
        acc[optionId] = label;
        return acc;
      }, {})
    : null;

  const flatTextCustomizations = textCustomizations
    ? textCustomizations.reduce((acc, { customizationId, options }) => {
        if (['font', 'color'].some((val) => val === customizationId)) {
          acc[customizationId] = options.reduce(
            (customizationAcc, { label, optionId }) => {
              customizationAcc[optionId] = label;
              return customizationAcc;
            },
            {}
          );
        }
        return acc;
      }, {})
    : null;

  return {
    ...parsedDetails,
    ...(textCustomizations
      ? {
          font: !fontIsNan
            ? flatTextCustomizations.font[parsedDetails.font]
            : parsedDetails.font,
          color: !colorIsNan
            ? flatTextCustomizations.color[parsedDetails.color]
            : parsedDetails.color,
        }
      : {}),
    ...(logoOptions && !logoIsNan
      ? {
          logo: flatLogoCustomizations[parsedDetails.logo],
        }
      : {}),
  };
};
